import type { WaveformPyramid } from "./types";

export const MAX_DETAIL_VISIBLE_SECONDS = 180;

export type DetailWaveformData = {
  pyramid: WaveformPyramid;
  startFrame: number;
  endFrame: number;
};

export type FrameRange = {
  startFrame: number;
  endFrame: number;
};

export function visibleFrameRange(
  centerFrame: number,
  visibleFrames: number,
  durationFrames: number,
): FrameRange {
  return {
    startFrame: Math.max(0, Math.round(centerFrame - visibleFrames / 2)),
    endFrame: Math.min(
      durationFrames,
      Math.round(centerFrame + visibleFrames / 2),
    ),
  };
}

export function detailDataCoversViewport(
  data: DetailWaveformData | null,
  centerFrame: number,
  visibleFrames: number,
  durationFrames: number,
): boolean {
  if (!data) return false;
  const visible = visibleFrameRange(centerFrame, visibleFrames, durationFrames);
  return (
    data.startFrame <= visible.startFrame && data.endFrame >= visible.endFrame
  );
}

export function planDetailRange(
  centerFrame: number,
  visibleFrames: number,
  durationFrames: number,
  sampleRate: number,
): FrameRange | null {
  if (
    durationFrames <= 0 ||
    visibleFrames / sampleRate > MAX_DETAIL_VISIBLE_SECONDS
  )
    return null;

  const minimumSpan = sampleRate * 30;
  const maximumSpan = sampleRate * 300;
  const span = Math.min(
    durationFrames,
    Math.max(
      visibleFrames,
      Math.min(maximumSpan, Math.max(minimumSpan, visibleFrames * 3)),
    ),
  );

  // Snap requests to overlapping windows. Playback updates the center several
  // times per second; without snapping, a debounce timer is continuously reset
  // and no detail request starts until playback stops.
  const step = Math.max(1, Math.round(Math.max(visibleFrames, span / 3)));
  const clampedCenter = Math.max(0, Math.min(durationFrames, centerFrame));
  const snappedCenter =
    Math.floor(clampedCenter / step) * step + Math.round(step / 2);
  let startFrame = Math.round(snappedCenter - span / 2);
  let endFrame = startFrame + span;

  if (startFrame < 0) {
    endFrame -= startFrame;
    startFrame = 0;
  }
  if (endFrame > durationFrames) {
    startFrame = Math.max(0, startFrame - (endFrame - durationFrames));
    endFrame = durationFrames;
  }

  return {
    startFrame: Math.max(0, Math.round(startFrame)),
    endFrame: Math.max(1, Math.round(endFrame)),
  };
}
